/**
 * Email verification landing — the target of the link Resend delivers.
 * Posts the token; success marks the address verified AND signs the user in
 * (the token proves ownership), then routes them to their home: hardware
 * providers to their provider page, software providers to the dashboard.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconArrowLeft } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.background.secondary};
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Card = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  box-shadow: 0 10px 40px rgba(13, 37, 61, 0.08);
  padding: clamp(28px, 5vw, 40px);
  text-align: center;
`;

const Brand = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: -0.3px;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
`;

const Message = styled.p`
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.5;
`;

const ErrorMessage = styled(Message)`
  color: ${({ theme }) => theme.color?.red ?? '#c0392b'};
`;

const ActionLink = styled(Link)`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.accent.primary};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.tertiary};
  text-decoration: none;
`;

function VerifyEmail() {
  const [state, setState] = useState<'verifying' | 'error'>('verifying');
  const [errorMsg, setErrorMsg] = useState('');
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — token is one-time.
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setErrorMsg('This verification link is incomplete — open the link from the email again.');
      setState('error');
      return;
    }

    verifyEmail(token)
      .then((user) => {
        if (!user) {
          // Verified, but two-factor setup happens at sign-in.
          navigate('/login?verified=1', { replace: true });
          return;
        }
        if (user.manufacturer_id != null) {
          navigate(`/hardware-providers/${user.manufacturer_id}`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch((err: any) => {
        setErrorMsg(
          err.response?.data?.error
          || 'Verification failed — the link may have expired.'
        );
        setState('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page>
      <Wrap>
        <Card>
          <Brand>Simkura</Brand>
          {state === 'verifying' ? (
            <Message>Verifying your email…</Message>
          ) : (
            <>
              <ErrorMessage>{errorMsg}</ErrorMessage>
              <Message>
                Links expire after 24 hours. You can request a fresh one from
                the sign-in page.
              </Message>
              <ActionLink to="/login">Go to sign in</ActionLink>
            </>
          )}
        </Card>
        <BackLink to="/">
          <IconArrowLeft size={15} /> Back to home
        </BackLink>
      </Wrap>
    </Page>
  );
}

export default VerifyEmail;
